import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import api from '../../../api';
import { PanelContainer } from '../../../components/panel';
import { color } from '../../../styles';
import {
  LAXATIVE_UNIT_EN,
  LAXATIVE_UNIT_JA,
  LAXATIVE_UNIT_LIST,
  LAXATIVE_TYPE_JA,
  LAXATIVE_TYPE_LIST,
  LAXATIVE_TYPE_EN,
} from '../../../constants';
import { IGetLaxativeRes } from '../../../types/api/care-settings/getLaxative';
import { IUpdateLaxativeParams } from '../../../types/api/care-settings/updateLaxative';
import { useTranslation } from 'react-i18next';

interface Props {
  url: string;
}

export interface IGetLaxative extends IGetLaxativeRes {
  displayUnit: string; // 日本語化された下剤の単位
}

interface ILaxativeListItem {
  label: string;
  value: string;
}

const TextFieldWrapper = css`
  margin-top: 24px;
`;

const TextFieldContainer = css`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const TextFieldTitle = css`
  font-size: 16px;
  width: 92px;
  margin-bottom: 24px;

  span {
    margin-right: 4px;
    color: ${color.red};
    font-size: 16px;
    font-weight: bold;
  }
`;

const StyledInput = css`
  max-width: 560px;
  width: 80%;
  height: 80px;
  margin-bottom: 6px;
`;

const ButtonContainer = css`
  display: flex;
  justify-content: center;
`;

const StyledButton = css`
  margin: 16px 0 16px 32px;
  min-width: 80px;
`;

export const UpdateLaxative = ({ url }: Props) => {
  const { t, i18n } = useTranslation();
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { laxativeId } = useParams<{ laxativeId: string }>();
  const [laxativeNameHelperText, setLaxativeNameHelperText] = React.useState<string>('');
  const [laxativeTypeHelperText, setLaxativeTypeHelperText] = React.useState<string>('');
  const [isPassedValidation, setIsPassedValidation] = React.useState<boolean>(false);
  const [onSend, setOnSend] = React.useState<boolean>(false);
  const [sendData, setSendData] = React.useState<IUpdateLaxativeParams>({ name: '', unit: '', type: '' });
  const history = useHistory();
  const [initLaxativeUnit, setInitLaxativeUnit] = React.useState<ILaxativeListItem | null>(null);
  const [initLaxativeName, setInitLaxativeName] = React.useState<string | null>(null);
  const [initLaxativeType, setInitLaxativeType] = React.useState<ILaxativeListItem | null>(null);
  const [pageTitle, setPageTitle] = React.useState<string>('');

  const laxativeUnitList = LAXATIVE_UNIT_LIST.map((v) => ({
    value: v,
    label: i18n.language.startsWith('ja') ? LAXATIVE_UNIT_JA[v] : LAXATIVE_UNIT_EN[v],
  }));

  const laxativeTypeList = LAXATIVE_TYPE_LIST.map((v) => ({
    value: v,
    label: i18n.language.startsWith('ja') ? LAXATIVE_TYPE_JA[v] : LAXATIVE_TYPE_EN[v],
  }));

  // Addの場合のフォームの初期値設定
  const initAddMode = () => {
    setPageTitle(t('addLaxative', '下剤の追加'));
    setInitLaxativeName('');
    setInitLaxativeType({ label: '', value: '' });
    setSendData({ name: '', unit: laxativeUnitList[0].value, type: '' });
    setInitLaxativeUnit(laxativeUnitList[0]);
  };

  // Editの場合のフォームの初期値設定
  const initEditMode = (laxative: IGetLaxativeRes) => {
    setPageTitle(t('editLaxative', '下剤の編集'));
    setInitLaxativeName(laxative.name);
    const foundUnit = laxativeUnitList.find((v) => {
      return v.value === laxative.unit;
    });
    const foundType = laxativeTypeList.find((v) => {
      return v.value === laxative.type;
    });
    setInitLaxativeUnit(foundUnit ?? laxativeUnitList[0]);
    setInitLaxativeType(foundType ?? { label: '', value: '' });

    const data = { name: laxative.name, unit: laxative.unit, type: laxative.type ?? '' };
    setSendData(data);
    checkValidation(data);
  };

  const fetchLaxative = async () => {
    const laxative = await api.get('/laxatives/:nursingHomeLaxativeId', {
      params: { nursingHomeLaxativeId: laxativeId },
    });
    return laxative;
  };

  const createLaxative = async () => {
    return await api.post('/nursing-homes/:nursingHomeId/laxatives', {
      nursingHomeId,
      name: sendData.name,
      unit: sendData.unit,
      type: sendData.type,
    });
  };

  const updateLaxative = async () => {
    return await api.put('/laxatives/:nursingHomeLaxativeId', {
      nursingHomeLaxativeId: laxativeId,
      name: sendData.name,
      unit: sendData.unit,
      type: sendData.type,
    });
  };

  const checkValidation = (data: IUpdateLaxativeParams) => {
    // エラーテキストをセット
    if (data.name) {
      setLaxativeNameHelperText('');
    } else {
      setLaxativeNameHelperText(t('laxativeNameIsRequired', '下剤名は必須入力です'));
    }
    if (data.type) {
      setLaxativeTypeHelperText('');
    } else {
      setLaxativeTypeHelperText(t('typeIsRequired', 'タイプは必須入力です'));
    }

    // バリデーションチェック
    if (data.name && data.type) {
      setIsPassedValidation(true);
      return true;
    } else {
      setIsPassedValidation(false);
      return false;
    }
  };

  const handleLaxativeNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const data = { ...sendData, name: event.target.value };
    setSendData(data);
    if (!isPassedValidation && !!laxativeNameHelperText) {
      checkValidation(data);
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    history.push(url);
  };

  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!checkValidation(sendData)) {
      return;
    }
    setOnSend(true);

    // paramsにidがある場合はput、そうでない場合はpostとして処理
    if (laxativeId) {
      await updateLaxative();
    } else {
      await createLaxative();
    }
    setTimeout(() => {
      setOnSend(false);
      history.push(url);
    }, 500);
  };

  const handleUnitPulldown = (e: React.SyntheticEvent, v: ILaxativeListItem) => {
    setSendData((prev) => {
      return { ...prev, unit: v.value };
    });
  };

  const handleTypePulldown = (e: React.SyntheticEvent, v: ILaxativeListItem) => {
    const data = { ...sendData, type: v.value };
    setSendData(data);
    if (!isPassedValidation && !!laxativeTypeHelperText) {
      checkValidation(data);
    }
  };

  React.useEffect(() => {
    // paramsにidがある場合はEdit、そうでない場合はAddとして処理
    if (laxativeId) {
      const init = async () => {
        const laxative = await fetchLaxative();
        initEditMode(laxative);
      };
      init();
    } else {
      initAddMode();
    }
  }, []);

  return (
    <PanelContainer title={pageTitle}>
      <div css={TextFieldWrapper}>
        <div css={TextFieldContainer}>
          {!(initLaxativeName === null) && (
            <>
              <div css={TextFieldTitle}>
                {t('name', '名前')}
                <span>*</span>:
              </div>
              <TextField
                css={StyledInput}
                id='laxativeName'
                label={t('laxativeName', '下剤名')}
                variant='outlined'
                onChange={handleLaxativeNameChange}
                type='text'
                autoComplete='off'
                defaultValue={initLaxativeName}
                helperText={laxativeNameHelperText}
                error={!isPassedValidation && !!laxativeNameHelperText}
              />
            </>
          )}
        </div>
        <div css={TextFieldContainer}>
          {initLaxativeUnit && (
            <>
              <div css={TextFieldTitle}>
                {t('unit', '単位')}
                <span>*</span>:
              </div>
              <Autocomplete
                css={StyledInput}
                onChange={handleUnitPulldown}
                disablePortal
                disableClearable={true}
                id='pulldown'
                options={laxativeUnitList}
                defaultValue={initLaxativeUnit}
                renderInput={(params) => <TextField {...params} label={t('unit', '単位')} />}
              />
            </>
          )}
        </div>
        <div css={TextFieldContainer}>
          {initLaxativeType && (
            <>
              <div css={TextFieldTitle}>
                {t('type', 'タイプ')}
                <span>*</span>:
              </div>
              <Autocomplete
                css={StyledInput}
                onChange={handleTypePulldown}
                disablePortal
                disableClearable
                id='pulldown'
                options={laxativeTypeList}
                defaultValue={initLaxativeType}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('type', 'タイプ')}
                    helperText={laxativeTypeHelperText}
                    error={!isPassedValidation && !!laxativeTypeHelperText}
                  />
                )}
              />
            </>
          )}
        </div>
      </div>
      <div css={ButtonContainer}>
        <Button css={StyledButton} color={'secondary'} onClick={handleCancel} variant='contained'>
          {t('cancel', 'キャンセル')}
        </Button>
        <Button css={StyledButton} onClick={handleSubmit} variant='contained'>
          OK
        </Button>
      </div>
    </PanelContainer>
  );
};
